import { useState } from 'react';
import { Filter, Search, TrendingDown, TrendingUp, Coins, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { AgentHeader } from '../../components/layout/AgentHeader';
import { AgentSidebar } from '../../components/layout/AgentSidebar';
import { BottomNav } from '../../components/layout/BottomNav';
import { TransactionCard } from '../../components/features/TransactionCard';
import { Modal } from '../../components/common/Modal';
import { useCashTransactions, TransactionFilters } from '../../hooks/useCashTransactions';
import { TransactionType, TransactionStatus } from '../../types/cash-transaction.types';

const TransactionListPage = () => {
  const [filters, setFilters] = useState<TransactionFilters>({
    page: 1,
    limit: 20,
  });
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filtros del modal (draft hasta que se apliquen)
  const [draftType, setDraftType] = useState<TransactionType | ''>('');
  const [draftStatus, setDraftStatus] = useState<string>('');
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');

  const { transactions, meta, summary, isLoading } = useCashTransactions(filters);

  const handleSearch = () => {
    setFilters((f) => ({ ...f, search: searchInput.trim() || undefined, page: 1 }));
  };

  const handleApplyFilters = () => {
    setFilters((f) => ({
      ...f,
      type: draftType || undefined,
      status: draftStatus || undefined,
      startDate: draftStart || undefined,
      endDate: draftEnd || undefined,
      page: 1,
    }));
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setDraftType('');
    setDraftStatus('');
    setDraftStart('');
    setDraftEnd('');
    setFilters({ page: 1, limit: 20 });
    setSearchInput('');
    setShowFilters(false);
  };

  const activeFilterCount = [filters.type, filters.status, filters.startDate, filters.search].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <AgentHeader />

      <div className="flex">
        <AgentSidebar />

        <main className="flex-1 p-4 lg:p-6 pb-20 lg:pb-6">
          <div className="max-w-4xl mx-auto space-y-5">

            {/* Header */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Transacciones</h2>
              <p className="text-sm text-gray-500">Historial completo de operaciones</p>
            </div>

            {/* Summary cards */}
            {summary && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingDown className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-gray-500">Cash-In</span>
                  </div>
                  <p className="text-lg font-bold text-green-600">{summary.cashInAmount.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">{summary.cashInCount} ops · XAF</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-red-600" />
                    <span className="text-xs text-gray-500">Cash-Out</span>
                  </div>
                  <p className="text-lg font-bold text-red-600">{summary.cashOutAmount.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">{summary.cashOutCount} ops · XAF</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Coins className="w-4 h-4 text-orange-500" />
                    <span className="text-xs text-gray-500">Comisiones</span>
                  </div>
                  <p className="text-lg font-bold text-orange-600">{summary.totalCommissions.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">XAF ganados</p>
                </div>
              </div>
            )}

            {/* Search + filter bar */}
            <div className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Nombre, teléfono o referencia..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Buscar
              </button>
              <button
                onClick={() => setShowFilters(true)}
                className="relative px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                <Filter size={16} />
                Filtros
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {/* Active filter chips */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2">
                {filters.search && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    "{filters.search}"
                    <button onClick={() => { setSearchInput(''); setFilters((f) => ({ ...f, search: undefined, page: 1 })); }}><X size={12} /></button>
                  </span>
                )}
                {filters.type && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    {filters.type === TransactionType.CASH_IN ? 'Cash-In' : 'Cash-Out'}
                    <button onClick={() => setFilters((f) => ({ ...f, type: undefined, page: 1 }))}><X size={12} /></button>
                  </span>
                )}
                {filters.status && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-medium">
                    {filters.status}
                    <button onClick={() => setFilters((f) => ({ ...f, status: undefined, page: 1 }))}><X size={12} /></button>
                  </span>
                )}
                {(filters.startDate || filters.endDate) && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                    {filters.startDate?.slice(0, 10)} → {filters.endDate?.slice(0, 10)}
                    <button onClick={() => setFilters((f) => ({ ...f, startDate: undefined, endDate: undefined, page: 1 }))}><X size={12} /></button>
                  </span>
                )}
                <button onClick={handleClearFilters} className="text-xs text-gray-500 underline hover:text-gray-800">
                  Limpiar todo
                </button>
              </div>
            )}

            {/* List */}
            <div className="space-y-3">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 h-20 animate-pulse" />
                ))
              ) : transactions.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <p className="text-gray-400 text-sm">No se encontraron transacciones</p>
                </div>
              ) : (
                transactions.map((transaction) => (
                  <TransactionCard key={transaction.id} transaction={transaction} />
                ))
              )}
            </div>

            {/* Pagination */}
            {meta.totalPages > 1 && (
              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-gray-500">
                  {((meta.page - 1) * meta.limit) + 1}–{Math.min(meta.page * meta.limit, meta.total)} de {meta.total}
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={meta.page <= 1}
                    onClick={() => setFilters((f) => ({ ...f, page: f.page! - 1 }))}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="px-3 py-2 text-sm font-medium text-gray-700">
                    {meta.page} / {meta.totalPages}
                  </span>
                  <button
                    disabled={!meta.hasMore}
                    onClick={() => setFilters((f) => ({ ...f, page: f.page! + 1 }))}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <BottomNav />

      {/* Filter Modal */}
      <Modal isOpen={showFilters} onClose={() => setShowFilters(false)} title="Filtros" size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={draftType}
              onChange={(e) => setDraftType(e.target.value as TransactionType | '')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value={TransactionType.CASH_IN}>Cash-In (Depósito)</option>
              <option value={TransactionType.CASH_OUT}>Cash-Out (Retiro)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              value={draftStatus}
              onChange={(e) => setDraftStatus(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value={TransactionStatus.PENDING}>Pendiente</option>
              <option value={TransactionStatus.COMPLETED}>Completada</option>
              <option value={TransactionStatus.FAILED}>Fallida</option>
              <option value="REVERSED">Revertida</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
              <input
                type="date"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value ? e.target.value + 'T00:00:00.000Z' : '')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
              <input
                type="date"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value ? e.target.value + 'T23:59:59.000Z' : '')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleClearFilters}
              className="flex-1 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Limpiar
            </button>
            <button
              onClick={handleApplyFilters}
              className="flex-1 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Aplicar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TransactionListPage;
